import React, { useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import { FaEnvelope, FaTimes, FaLink, FaPaperPlane, FaCopy } from "react-icons/fa";
import { API_BASE } from "../../../ApiUrl";
import "./Invitation.css";

const REGISTER_LINK = "http://145.79.0.94:83/Oceanstar/register-stage1";

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

const UserInvitation = () => {
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [error, setError] = useState("");
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);

  const addEmail = (rawValue) => {
    const value = rawValue.trim().replace(/,$/, "");
    if (!value) return;

    if (!isValidEmail(value)) {
      setError(`"${value}" is not a valid email address`);
      return;
    }
    if (emails.includes(value)) {
      setError(`"${value}" has already been added`);
      return;
    }

    setEmails((prev) => [...prev, value]);
    setEmailInput("");
    setError("");
  };

  const handleInputKeyDown = (e) => {
    if (["Enter", ",", "Tab"].includes(e.key)) {
      if (emailInput.trim()) {
        e.preventDefault();
        addEmail(emailInput);
      }
    } else if (e.key === "Backspace" && !emailInput && emails.length > 0) {
      setEmails((prev) => prev.slice(0, -1));
    }
  };

  const handleInputBlur = () => {
    if (emailInput.trim()) {
      addEmail(emailInput);
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text");
    if (/[,\s;]/.test(pasted)) {
      e.preventDefault();
      const parts = pasted.split(/[,\s;]+/).filter(Boolean);
      parts.forEach((part) => addEmail(part));
    }
  };

  const removeEmail = (emailToRemove) => {
    setEmails((prev) => prev.filter((e) => e !== emailToRemove));
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(REGISTER_LINK);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (emailInput.trim()) {
      addEmail(emailInput);
    }

    if (emails.length === 0 && !emailInput.trim()) {
      setError("Add at least one tenant email before sending invitations.");
      return;
    }

    setLoading(true);
    try {
      const payload =
        emails.length === 1
          ? {
              email: emails[0],
              registration_url: REGISTER_LINK,
            }
          : emails.map((email) => ({
              email,
              registration_url: REGISTER_LINK,
            }));

      await axios.post(`${API_BASE}/tenant-reg-invitations/`, payload);

      Swal.fire({
        icon: "success",
        title: "Invitation Sent",
        text: `Invitation${emails.length > 1 ? "s" : ""} sent to ${emails.length} tenant${
          emails.length > 1 ? "s" : ""
        } successfully.`,
        confirmButtonColor: "#2f5fd8",
      });

      setEmails([]);
      setEmailInput("");
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to Send Invitation",
        text:
          err.response?.data?.message ||
          "Something went wrong while sending invitations. Please try again.",
        confirmButtonColor: "#2f5fd8",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="invite-container">
          <div className="invite-card">
            <div className="invite-card__header">
              <div className="invite-card__icon">
                <FaPaperPlane />
              </div>
              <div>
                <h2 className="invite-card__title">Invite Tenants</h2>
                <p className="invite-card__subtitle">
                  Send registration invitations to one or more tenants via email.
                </p>
              </div>
            </div>

            <form className="invite-form" onSubmit={handleSubmit}>
              {/* Tenant Emails */}
              <div className="invite-form__group">
                <label className="invite-form__label">
                  Tenant Email(s) <span className="invite-form__required">*</span>
                </label>

                <div className="invite-email-input" onClick={() => document.getElementById("invite-email-field")?.focus()}>
                  {emails.map((email) => (
                    <span className="invite-email-chip" key={email}>
                      <FaEnvelope className="invite-email-chip__icon" />
                      {email}
                      <button
                        type="button"
                        className="invite-email-chip__remove"
                        onClick={() => removeEmail(email)}
                        aria-label={`Remove ${email}`}
                      >
                        <FaTimes />
                      </button>
                    </span>
                  ))}
                  <input
                    id="invite-email-field"
                    type="text"
                    className="invite-email-input__field"
                    placeholder={
                      emails.length === 0
                        ? "Enter email and press Enter or comma (e.g. tenant@company.com)"
                        : "Add another email…"
                    }
                    value={emailInput}
                    onChange={(e) => {
                      setEmailInput(e.target.value);
                      if (error) setError("");
                    }}
                    onKeyDown={handleInputKeyDown}
                    onBlur={handleInputBlur}
                    onPaste={handlePaste}
                  />
                </div>

                <small className="invite-form__hint">
                  Press <strong>Enter</strong>, <strong>comma</strong>, or paste a list of emails
                  to add multiple tenants.
                </small>

                {error && <span className="invite-form__error">{error}</span>}
              </div>

              {/* Registration Link (fixed / read-only) */}
              <div className="invite-form__group">
                <label className="invite-form__label">Registration Link</label>
                <div className="invite-link-box">
                  <FaLink className="invite-link-box__icon" />
                  <span className="invite-link-box__url">{REGISTER_LINK}</span>
                  <button
                    type="button"
                    className="invite-link-box__copy"
                    onClick={handleCopyLink}
                  >
                    <FaCopy /> {copied ? "Copied!" : "Copy"}
                  </button>
                </div>
                <small className="invite-form__hint">
                  This link will be included in every invitation email sent.
                </small>
              </div>

              {/* Actions */}
              <div className="invite-form__actions">
                <span className="invite-form__count">
                  {emails.length} tenant{emails.length !== 1 ? "s" : ""} added
                </span>
                <button
                  type="submit"
                  className="invite-form__submit"
                  disabled={loading}
                >
                  {loading ? (
                    "Sending..."
                  ) : (
                    <>
                      <FaPaperPlane /> Send Invitation
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserInvitation;